import { useEffect, useMemo, useState } from "react";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import { listAdminClubs, listClubs } from "../../clubs/shared/services/clubsService";
import type { Club } from "../../clubs/shared/types/club";
import {
  buildCategoryAddressValue,
  buildClubLocality,
  buildClubSummary,
  buildEventAddressValue,
} from "../utils/venueFields";

type AdminVenueFieldsProps = {
  address: string;
  addressLabel?: string;
  addressPlaceholder?: string;
  city?: string;
  className?: string;
  mode?: "full" | "address-only";
  onAddressChange: (value: string) => void;
  onCityChange?: (value: string) => void;
  onStateChange?: (value: string) => void;
  state?: string;
  token?: string | null;
};

function normalizeText(value: unknown) {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function findMatchingClub(
  clubs: Club[],
  mode: "full" | "address-only",
  address: string,
  city?: string,
  state?: string,
) {
  const normalizedAddress = normalizeText(address);
  const normalizedCity = normalizeText(city);
  const normalizedState = normalizeText(state);

  return (
    clubs.find((club) => {
      const expectedAddress =
        mode === "address-only"
          ? buildCategoryAddressValue(club)
          : buildEventAddressValue(club);
      if (normalizeText(expectedAddress) !== normalizedAddress) return false;
      if (mode === "address-only") return true;
      return (
        normalizeText(club.city) === normalizedCity &&
        normalizeText(club.state) === normalizedState
      );
    }) ||
    clubs.find((club) => {
      if (!normalizedAddress.includes(normalizeText(club.name))) return false;
      if (mode === "address-only") return true;
      return (
        normalizeText(club.city) === normalizedCity &&
        normalizeText(club.state) === normalizedState
      );
    }) ||
    null
  );
}

export default function AdminVenueFields({
  address,
  addressLabel,
  addressPlaceholder,
  city = "",
  className = "",
  mode = "full",
  onAddressChange,
  onCityChange,
  onStateChange,
  state = "",
  token,
}: AdminVenueFieldsProps) {
  const createTexts = siteTexts.admin.createChallengePage;
  const [clubs, setClubs] = useState<Club[]>([]);
  const [clubsLoading, setClubsLoading] = useState(false);
  const [selectedClubId, setSelectedClubId] = useState("");

  useEffect(() => {
    let cancelled = false;
    setClubsLoading(true);
    const request = token ? listAdminClubs(token) : listClubs({});
    request
      .then((response) => {
        if (cancelled) return;
        setClubs(Array.isArray(response.clubs) ? response.clubs : []);
      })
      .catch(() => {
        if (cancelled) return;
        setClubs([]);
      })
      .finally(() => {
        if (cancelled) return;
        setClubsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const matchingClub = findMatchingClub(clubs, mode, address, city, state);
    setSelectedClubId(matchingClub ? String(matchingClub.id) : "");
  }, [address, city, clubs, mode, state]);

  const selectedClub = useMemo(
    () =>
      clubs.find((club) => String(club.id) === String(selectedClubId)) || null,
    [clubs, selectedClubId],
  );

  const handleSelectClub = (clubId: string) => {
    setSelectedClubId(clubId);
    if (!clubId) return;
    const club =
      clubs.find((item) => String(item.id) === String(clubId)) || null;
    if (!club) return;
    onAddressChange(
      mode === "address-only"
        ? buildCategoryAddressValue(club)
        : buildEventAddressValue(club),
    );
    if (mode === "full") {
      onCityChange?.(String(club.city || ""));
      onStateChange?.(String(club.state || ""));
    }
  };

  return (
    <div
      className={[
        "admin-venue-fields",
        mode === "address-only"
          ? "admin-venue-fields--address-only"
          : "admin-venue-fields--full",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <label className="field admin-venue-fields__selector">
        <span>{createTexts.selectClubVenue}</span>
        <select
          value={selectedClubId}
          onChange={(event) => handleSelectClub(event.target.value)}
          disabled={clubsLoading || clubs.length === 0}
        >
          <option value="">
            {clubsLoading
              ? createTexts.loadingClubs
              : clubs.length > 0
                ? createTexts.manualVenueOption
                : createTexts.noRegisteredClubs}
          </option>
          {clubs.map((club) => {
            const locality = buildClubLocality(club);
            return (
              <option key={club.id} value={String(club.id)}>
                {locality ? `${club.name} - ${locality}` : club.name}
              </option>
            );
          })}
        </select>
      </label>

      {selectedClub ? (
        <div className="admin-venue-fields__summary">
          <strong>{selectedClub.name}</strong>
          <span>{buildClubSummary(selectedClub)}</span>
        </div>
      ) : null}

      {mode === "full" ? (
        <div className="admin-row-two admin-form-block-location">
          <label className="field">
            <span>{createTexts.city}</span>
            <input
              type="text"
              value={city}
              onChange={(event) => onCityChange?.(event.target.value)}
              placeholder={createTexts.eventCityPlaceholder}
              required
            />
          </label>
          <label className="field">
            <span>{createTexts.state}</span>
            <input
              type="text"
              value={state}
              onChange={(event) => onStateChange?.(event.target.value)}
              placeholder="SC"
              maxLength={32}
            />
          </label>
        </div>
      ) : null}

      <label className="field admin-form-block-address">
        <span>{addressLabel || createTexts.clubVenue}</span>
        <input
          type="text"
          value={address}
          onChange={(event) => onAddressChange(event.target.value)}
          placeholder={
            addressPlaceholder || createTexts.eventClubVenuePlaceholder
          }
          required
        />
      </label>
    </div>
  );
}
